import { useState } from 'react'

import {
  Field,
  Label,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Text
} from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { OptionSelect } from '../../../components/advertiserBrand/OptionSelect'
import styles from '../../../components/advertiserBrand/styles.module.css'
import { toIdentities } from '../../../helpers/gtin'
import { useAdvertiserBrandProductMutations } from '../../../hooks/useAdvertiserBrandProducts'
import { useAdvertiserBrandOptions } from '../../../hooks/useAdvertiserBrands'

type LinkBrandModalProps = {
  open: boolean
  products: AdvertiserBrandProduct[]
  onClose: () => void
  /** Só depois de gravar: cancelar não conta. */
  onSuccess?: () => void
}

/**
 * Vínculo em massa a outra marca — reatribui (1 produto tem no máximo 1 marca
 * por advertiser), substituindo qualquer vínculo anterior.
 */
export const LinkBrandModal: React.FC<LinkBrandModalProps> = ({
  open,
  products,
  onClose,
  onSuccess
}) => {
  const { t } = useAPPTranslation()
  const { options } = useAdvertiserBrandOptions()
  const { moveProducts } = useAdvertiserBrandProductMutations()

  const [brand, setBrand] = useState('')

  // o modal fica montado entre aberturas: a marca escolhida não pode vazar
  // para a próxima seleção
  const close = () => {
    setBrand('')
    onClose()
  }

  const handleSubmit = async () => {
    // decisão tomada pela pessoa na tela, mesmo quando a sugestão coincide
    await moveProducts.mutateAsync({
      brand,
      ...toIdentities(products),
      source: 'manual'
    })

    onSuccess?.()
    close()
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="small"
      className={styles.brandModal}
    >
      <ModalHeader>
        <ModalHeading>
          {t('rm:advertiserProducts.linkBrand.title')}
        </ModalHeading>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent>
        <Text variant="body">
          {t('rm:advertiserProducts.linkBrand.description', {
            count: products.length
          })}
        </Text>

        <Field className={styles.brandField} style={{ marginTop: 16 }}>
          <Label>{t('rm:advertiserProducts.linkBrand.brand.label')}</Label>
          <OptionSelect
            placeholder={t('rm:advertiserProducts.linkBrand.brand.placeholder')}
            options={options}
            isClearable={false}
            value={brand}
            onChange={option => setBrand(option?.value ?? '')}
          />
        </Field>
      </ModalContent>

      <ModalFooter>
        <Button template="transparent" size="medium" onClick={close}>
          {t('rm:button.Cancel')}
        </Button>
        <Button
          template="primary"
          size="medium"
          disabled={!brand || !products.length}
          loading={moveProducts.isPending}
          onClick={handleSubmit}
        >
          {t('rm:advertiserProducts.linkBrand.submit', {
            count: products.length
          })}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
